import * as React from 'react';

import { MAX_QUIZ_NAME_LENGTH } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { AlertMessage } from '~shared/components/AlertMessage';

import * as Constants from '../../../../../../shared/Constants';
import { ImportSpreadsheetErrors } from '../../../api/quiz/importQuiz';

interface ErrorProps {
  error: any;
}

/* copied verbatim from ImportQuizView.tsx */
const importExcelErrorMessages = {
  [ImportSpreadsheetErrors.FILE_TOO_LARGE]: () =>
    translate('The file size limit is 1 megabyte (MB).'),

  [ImportSpreadsheetErrors.QUIZ_NAME_MISSING]: (value) =>
    translate('Your Excel template is missing a quiz name at cell {0}.').format(
      value
    ),

  [ImportSpreadsheetErrors.QUIZ_NAME_TOO_LONG]: (value) =>
    translate(
      'The quiz name at cell {0} must not exceed {1} characters.'
    ).format(value, MAX_QUIZ_NAME_LENGTH),

  [ImportSpreadsheetErrors.QUESTIONS_MISSING]: () =>
    translate('The quiz has no questions.'),

  [ImportSpreadsheetErrors.TOO_MANY_QUESTIONS]: (value) =>
    translate('The quiz must not exceed {0} questions.').format(value),

  [ImportSpreadsheetErrors.QUESTION_TYPE_MISSING]: (value) =>
    translate('Please choose a question type at cell(s) {0}.').format(
      value.join(', ')
    ),

  [ImportSpreadsheetErrors.QUESTION_TEXT_MISSING]: (value) =>
    translate('The question text is empty at cell(s) {0}.').format(
      value.join(', ')
    ),

  [ImportSpreadsheetErrors.QUIZ_QUESTION_TOO_LONG]: (value) =>
    translate(
      'The question text at cell(s) {0} exceeds the maximum number of characters ({1}).'
    ).format(value.join(', '), Constants.MAX_QUESTION_LENGTH),

  [ImportSpreadsheetErrors.QUIZ_ANSWER_TOO_LONG]: (value) =>
    translate(
      'The answer text at cell(s) {0} exceeds the maximum number of characters ({1}).'
    ).format(value.join(', '), Constants.MAX_ANSWER_LENGTH),

  [ImportSpreadsheetErrors.NOT_ENOUGH_ANSWERS]: (value) =>
    translate(
      'The multiple-choice question at row(s) {0} must have at least two answers.'
    ).format(value.join(', ')),

  [ImportSpreadsheetErrors.INVALID_QUESTION_TYPE]: (value) =>
    translate('The question type at cell(s) {0} is not valid.').format(
      value.join(', ')
    ),
};
const handledErrors = Object.keys(importExcelErrorMessages);

export function ImportError({ error }: ErrorProps) {
  if (!error) return null;

  if (!error.isAxiosError) {
    return (
      <AlertMessage tone="critical" showIcon>
        {translate('Please check the file and try again.')}
      </AlertMessage>
    );
  }

  const excelErrors = Object.entries<any>(error.response.data.errors).filter(
    ([key]) => handledErrors.includes(key)
  );

  if (excelErrors.length === 0) {
    return (
      <AlertMessage tone="critical" showIcon>
        {translate('Please check the file and try again.')}
      </AlertMessage>
    );
  }

  return (
    <AlertMessage tone={'critical'} showIcon>
      <strong>{translate('Excel Error(s)')}</strong>
      <ul>
        {excelErrors.map(([key, value]) => {
          return <li key={key}>{importExcelErrorMessages[key](value)}</li>;
        })}
      </ul>
    </AlertMessage>
  );
}
